import { useMemo, useState } from "react";
import { useMenu } from "@/context/MenuContext";
import { useBranch } from "@/context/BranchContext";
import AccessDenied from "@/pages/branch/access-denied/page";
import CategorySection from "./components/CategorySection";

export default function MenuPage() {
  const { can } = useBranch();
  const {
    categories,
    products,
    toggleAvailable,
    setQuantity,
    markOutOfStock,
    disableAll,
    enableAll,
  } = useMenu();

  const canAvailability = can("productAvailability");
  const canQuantities = can("editQuantities");

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [disableArmed, setDisableArmed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2600);
  };

  const availableCount = useMemo(
    () => products.filter((p) => p.available !== false && p.quantity !== 0).length,
    [products],
  );

  if (!canAvailability && !canQuantities) {
    return <AccessDenied />;
  }

  const toggleCategory = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDisableAll = () => {
    if (!disableArmed) {
      setDisableArmed(true);
      window.setTimeout(() => setDisableArmed(false), 3000);
      return;
    }
    disableAll();
    setDisableArmed(false);
    showToast("تم تعطيل جميع المنتجات");
  };

  const handleEnableAll = () => {
    enableAll();
    showToast("تم تفعيل جميع المنتجات");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* معلومات المنيو */}
      <div className="flex items-start gap-3 px-4 py-3.5 rounded-card border border-secondary-200 bg-secondary-50">
        <div className="w-6 h-6 flex items-center justify-center text-secondary-500 shrink-0 mt-0.5">
          <i className="ri-information-line text-xl"></i>
        </div>
        <p className="text-sm text-secondary-700 leading-relaxed">
          المنيو الرئيسي يُدار من لوحة صاحب المحل — يمكنك التحكم في توفر المنتجات
          لهذا الفرع فقط.
        </p>
      </div>

      {/* ملخص + إجراءات جماعية */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-primary-500"></div>
          <span className="text-sm font-semibold text-foreground-700">
            {availableCount} منتج متاح من أصل {products.length}
          </span>
        </div>

        {canAvailability ? (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleEnableAll}
              className="h-11 px-4 rounded-btn text-sm font-bold text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none"
            >
              تفعيل الكل
            </button>
            <button
              type="button"
              onClick={handleDisableAll}
              className={`h-11 px-4 rounded-btn text-sm font-bold transition-colors cursor-pointer whitespace-nowrap border flex-1 sm:flex-none ${
                disableArmed
                  ? "bg-red-500 border-red-500 text-white"
                  : "text-red-600 border-red-200 hover:bg-red-50"
              }`}
            >
              {disableArmed ? "تأكيد التعطيل؟" : "تعطيل الكل"}
            </button>
          </div>
        ) : null}
      </div>

      {/* أقسام المنيو */}
      <div className="space-y-3">
        {categories.map((category) => {
          const categoryProducts = products.filter(
            (p) => p.categoryId === category.id,
          );
          if (categoryProducts.length === 0) return null;
          return (
            <CategorySection
              key={category.id}
              category={category}
              products={categoryProducts}
              collapsed={Boolean(collapsed[category.id])}
              canEditAvailability={canAvailability}
              canEditQuantities={canQuantities}
              onToggle={() => toggleCategory(category.id)}
              onToggleAvailable={(id) => {
                toggleAvailable(id);
                showToast("تم تحديث توفّر المنتج");
              }}
              onSetQuantity={setQuantity}
              onMarkOutOfStock={(id) => {
                markOutOfStock(id);
                showToast("تم تعطيل المنتج (نفد)");
              }}
            />
          );
        })}
      </div>

      {/* Toast */}
      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-4 py-3 rounded-btn bg-foreground-900 text-white shadow-dropdown animate-fade-slide-up">
          <div className="w-5 h-5 flex items-center justify-center text-primary-400">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </div>
          <span className="text-sm font-semibold whitespace-nowrap">{toast}</span>
        </div>
      ) : null}
    </div>
  );
}